import { create } from 'zustand';
import { FavoriteResponse } from '@/lib/types/customer/favorites';
import { Store } from '@/lib/types/customer/restaurant/StoreProfile';
import { RestaurantEvent } from '@/lib/types/customer/RestaurantEvents';
import { RestaurantJob } from '@/lib/types/customer/RestaurantJobs';

type State = {
  favoritesData: FavoriteResponse;
  favoriteRestaurantIDs: number[];
  favoriteEventIDs: number[];
  favoriteJobIDs: number[];
  isLoading?: boolean;
  error?: any;
  favoriteStores: Store[];
  favoriteOnlineShops: Store[];
  favoriteEvents: RestaurantEvent[];
  favoriteJobs: RestaurantJob[];
};

type Actions = {
  setData: (data: FavoriteResponse) => void;
  setIsLoading: (isLoading: boolean) => void;
  getIsFavorite(type: 'restaurant' | 'event' | 'job', id: number): boolean;
  addToFavoriteState: (type: string, id: number) => void;
  removeFromFavoriteState: (type: string, id: number) => void;
};

export const useCustomerFavoritesStore = create<State & Actions>(
  (set, get) => ({
    isLoading: true,
    error: null,
    favoritesData: {
      restaurant: [],
      event: [],
      job: [],
    },
    favoriteRestaurantIDs: [],
    favoriteEventIDs: [],
    favoriteJobIDs: [],
    favoriteStores: [],
    favoriteOnlineShops: [],
    favoriteEvents: [],
    favoriteJobs: [],
    setIsLoading: (isLoading: boolean) => {
      set({ isLoading });
    },
    setData: (data: FavoriteResponse) => {
      const { restaurant, event, job } = data;
      set({
        favoritesData: data,
        favoriteRestaurantIDs:
          restaurant?.length > 0 ? restaurant.map((r) => r.id) : [],
        favoriteEventIDs: event?.length > 0 ? event.map((e) => e.id) : [],
        favoriteJobIDs: job?.length > 0 ? job.map((j) => j.id) : [],
        favoriteStores:
          restaurant?.length > 0
            ? restaurant.filter((item) => item.type === 'restaurant')
            : [],
        favoriteOnlineShops:
          restaurant?.length > 0
            ? restaurant.filter((item) => item.type === 'online_shop')
            : [],
        favoriteEvents: event || [],
        favoriteJobs: job || [],
      });
    },
    getIsFavorite: (type, id) => {
      const state = get();
      switch (type) {
        case 'restaurant':
          return state.favoriteRestaurantIDs.includes(id);
        case 'event':
          return state.favoriteEventIDs.includes(id);
        case 'job':
          return state.favoriteJobIDs.includes(id);
        default:
          return false;
      }
    },
    addToFavoriteState: (type: string, id: number) => {
      const state = get();
      switch (type) {
        case 'restaurant': {
          const updatedResIDs = [...state.favoriteRestaurantIDs, id];
          set({ favoriteRestaurantIDs: updatedResIDs });
          break;
        }
        case 'event': {
          const updatedEventIDs = [...state.favoriteEventIDs, id];
          set({ favoriteEventIDs: updatedEventIDs });
          break;
        }
        case 'job': {
          const updatedJobIds = [...state.favoriteJobIDs, id];
          set({ favoriteJobIDs: updatedJobIds });
          break;
        }
      }
    },
    removeFromFavoriteState: (type: string, id: number) => {
      const state = get();
      switch (type) {
        case 'restaurant': {
          const updatedResIDs = state.favoriteRestaurantIDs.filter(
            (item) => item !== id
          );
          const updatedFavoriteStores = state.favoriteStores.filter(
            (item) => item.id !== id
          );
          const updatedFavoriteOnlineShops = state.favoriteOnlineShops.filter(
            (item) => item.id !== id
          );
          set({
            favoriteRestaurantIDs: updatedResIDs,
            favoriteStores: updatedFavoriteStores,
            favoriteOnlineShops: updatedFavoriteOnlineShops,
          });
          break;
        }
        case 'event': {
          const updatedEventIDs = state.favoriteEventIDs.filter(
            (item) => item !== id
          );
          const updatedFavoriteEvents = state.favoriteEvents.filter(
            (item) => item.id !== id
          );
          set({
            favoriteEventIDs: updatedEventIDs,
            favoriteEvents: updatedFavoriteEvents,
          });
          break;
        }
        case 'job': {
          const updatedJobIds = state.favoriteJobIDs.filter(
            (item) => item !== id
          );
          const updatedFavoriteJobs = state.favoriteJobs.filter(
            (item) => item.id !== id
          );
          set({
            favoriteJobIDs: updatedJobIds,
            favoriteJobs: updatedFavoriteJobs,
          });
          break;
        }
      }
    },
  })
);
